import { Box, type TooltipProps, useMediaQuery } from "@mui/material";
import { type FC, type KeyboardEvent, type MouseEvent, type ReactElement, type ReactNode, type SyntheticEvent, useCallback, useState } from "react";
import { HoverCard } from "./HoverCard";
import { InHouseMobileSheet } from "./InHouseMobileSheet";

/** Where the content is being drawn, so it can size itself: a card has a reading measure, a sheet is full width. */
export enum HoverCardOrSheetSurface {
	Card = "card",
	Sheet = "sheet",
}

/** What the trigger is handed: whether its card or sheet is showing, and the click it must wire. */
export type HoverCardOrSheetTrigger = {
	isOpen: boolean;
	/** Keeps the click at the trigger (a tappable card behind it keeps its own tap) and, on touch, opens the sheet. */
	onClick: (event: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>) => void;
	/**
	 * Spread onto the trigger: on touch it opens a dialog (the sheet), so it says so and whether it is open. Empty
	 * where a mouse hovers it — the hover card describes the trigger instead (`HoverCard`'s `describeChild`).
	 */
	ariaProps: { "aria-haspopup"?: "dialog"; "aria-expanded"?: boolean };
};

type Props = {
	/** The card's body for the surface it is drawn on. Mounted only while open, in the card or the sheet alike. */
	content: (surface: HoverCardOrSheetSurface) => ReactNode;
	/** Renders the trigger: one ref-holding element that spreads the props it is given (see `HoverCard`). */
	children: (trigger: HoverCardOrSheetTrigger) => ReactElement;
	/** DEFAULT: `HoverCard`'s own, `bottom-start`. */
	placement?: TooltipProps["placement"];
	/** Lifts the phone sheet above a bar pinned to the bottom (a bottom nav). Absent = the screen's bottom edge. */
	sheetBottomOffset?: string;
	/** Told whenever the card or the sheet opens or closes. Absent = not told. */
	onOpenChange?: (open: boolean) => void;
	dataTestId: string;
};

const FINE_POINTER_QUERY = "(hover: hover) and (pointer: fine)";

const stopPropagation = (event: SyntheticEvent): void => event.stopPropagation();

/**
 * The same read-only card behind a trigger on every device: a `HoverCard` where there is a mouse (hover or keyboard
 * focus opens it), a bottom sheet where there is not (a tap opens it). The trigger is a render prop, so any focusable
 * element can be one.
 *
 * Clicks never leave it: the trigger usually sits inside something tappable (a list card that opens its record), and
 * React bubbles a click inside the portalled sheet up to that card too — so both the trigger's and the sheet's clicks
 * stop here.
 */
export const HoverCardOrSheet: FC<Props> = ({ content, children, placement, sheetBottomOffset, onOpenChange, dataTestId }) => {
	const hasFinePointer = useMediaQuery(FINE_POINTER_QUERY, { noSsr: true });
	const [isOpen, setIsOpen] = useState<boolean>(false);
	// The sheet mounts on the first open and then stays, so it can play its exit — a list of triggers costs no drawers.
	const [hasOpened, setHasOpened] = useState<boolean>(false);

	const changeOpen = useCallback(
		(next: boolean): void => {
			setIsOpen(next);
			if (next) setHasOpened(true);
			onOpenChange?.(next);
		},
		[onOpenChange],
	);

	const handleClick = useCallback(
		(event: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>): void => {
			event.stopPropagation();
			if (!hasFinePointer) changeOpen(true);
		},
		[hasFinePointer, changeOpen],
	);

	const ariaProps: HoverCardOrSheetTrigger["ariaProps"] = hasFinePointer ? {} : { "aria-haspopup": "dialog", "aria-expanded": isOpen };
	const trigger = children({ isOpen, onClick: handleClick, ariaProps });

	// `display: contents`: the wrappers add no box to the line the trigger sits in, they are only event boundaries. The
	// trigger's press events stop too, so a tappable ancestor (a `ButtonBase` card header) does not ripple under it. The
	// sheet's stop only at click: its swipe-to-close listens for touches on the document.
	const triggerBoundary = (
		<Box component="span" onClick={stopPropagation} onMouseDown={stopPropagation} onTouchStart={stopPropagation} sx={{ display: "contents" }}>
			{hasFinePointer ? (
				<HoverCard content={content(HoverCardOrSheetSurface.Card)} placement={placement} onOpenChange={changeOpen} dataTestId={dataTestId}>
					{trigger}
				</HoverCard>
			) : (
				trigger
			)}
		</Box>
	);

	if (hasFinePointer || !hasOpened) return triggerBoundary;

	return (
		<>
			{triggerBoundary}
			<Box component="span" onClick={stopPropagation} sx={{ display: "contents" }}>
				<InHouseMobileSheet
					open={isOpen}
					onClose={() => changeOpen(false)}
					showHeader={false}
					swipeToClose
					showHandle
					fitContent
					bottomOffset={sheetBottomOffset}
					contentSx={{ p: 0, pb: 2 }}
					dataTestId={`${dataTestId}-sheet`}
				>
					<Box data-testid={dataTestId}>{content(HoverCardOrSheetSurface.Sheet)}</Box>
				</InHouseMobileSheet>
			</Box>
		</>
	);
};
